import { toast } from "@/components/ui/sonner";
import { UserType } from "@/constants";
import {
  createLicenseTypeQuestionMutation,
  getGovernmentJurisdictionOptions,
  getGovernmentLicenseTypeConfigurationsOptions,
  getJurisdictionsOptions,
  getLicenseTypeQuestionOptions,
  getLicenseTypesOptions,
  updateLicenseTypeQuestionMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type { QuestionScope, QuestionType } from "@/hera-api/client/types.gen";
import { dropForeclosureCaseListQueries } from "@/lib/foreclosure-case-query-invalidation";
import { isGovernmentPortalViewer } from "@/lib/government-impersonate";
import { getQueryErrorMessage, getUserType } from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthQuerySetup } from "./use-auth-query-setup";

function companyShowInListPayload(data: {
  answer_type?: string | null;
  show_in_list?: boolean | null;
  contact_name_show_in_list?: boolean | null;
  contact_email_show_in_list?: boolean | null;
  contact_phone_show_in_list?: boolean | null;
  contact_address_show_in_list?: boolean | null;
}) {
  const company = data.answer_type === "company_contact";
  return {
    show_in_list: data.show_in_list ?? false,
    contact_name_show_in_list: company
      ? (data.contact_name_show_in_list ?? false)
      : false,
    contact_company_name_show_in_list: false,
    contact_email_show_in_list: company
      ? (data.contact_email_show_in_list ?? false)
      : false,
    contact_phone_show_in_list: company
      ? (data.contact_phone_show_in_list ?? false)
      : false,
    contact_address_show_in_list: company
      ? (data.contact_address_show_in_list ?? false)
      : false,
  };
}

export function useQuestionsForm(
  scope: QuestionScope | null,
  questionId: string | null,
  questionType: QuestionType = "registration",
) {
  const queryClient = useQueryClient();
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const { user } = useAuth0();
  const userType = getUserType(user);
  const isOperator =
    userType === UserType.OPERATOR && !isGovernmentPortalViewer(userType);
  const isGovernment = isGovernmentPortalViewer(userType);

  // Get question
  const {
    data: questionData,
    isLoading: isLoadingQuestion,
    error: errorQuestion,
  } = useQuery({
    ...getLicenseTypeQuestionOptions({
      path: {
        question_scope: scope as QuestionScope,
        question_id: questionId as string,
      },
      query: {
        include_deleted: true,
      },
    }),
    enabled: isAuthSetupComplete && !!scope && !!questionId,
  });

  // For operators, fetch all jurisdictions
  // For government users, fetch only their own jurisdiction
  const {
    data: operatorJurisdictionsData,
    isLoading: isLoadingOperatorJurisdictions,
    error: errorOperatorJurisdictions,
  } = useQuery({
    ...getJurisdictionsOptions({}),
    enabled: isAuthSetupComplete && isOperator,
  });

  const {
    data: governmentJurisdictionData,
    isLoading: isLoadingGovernmentJurisdiction,
    error: errorGovernmentJurisdiction,
  } = useQuery({
    ...getGovernmentJurisdictionOptions({}),
    enabled: isAuthSetupComplete && isGovernment,
  });

  // Combine jurisdictions data based on user type
  const jurisdictionsData = isOperator
    ? operatorJurisdictionsData
    : isGovernment && governmentJurisdictionData
      ? { jurisdictions: [governmentJurisdictionData] }
      : null;
  const isLoadingJurisdictions = isOperator
    ? isLoadingOperatorJurisdictions
    : isLoadingGovernmentJurisdiction;
  const errorJurisdictions = isOperator
    ? errorOperatorJurisdictions
    : errorGovernmentJurisdiction;

  const {
    data: licenseTypesData,
    isLoading: isLoadingLicenseTypes,
    error: errorLicenseTypes,
  } = useQuery({
    ...getLicenseTypesOptions({}),
    enabled: isAuthSetupComplete && !isGovernment,
  });

  // For government users, only license types configured for their jurisdiction
  const {
    data: governmentLicenseTypeConfigsData,
    isLoading: isLoadingGovernmentLicenseTypeConfigs,
    error: errorGovernmentLicenseTypeConfigs,
  } = useQuery({
    ...getGovernmentLicenseTypeConfigurationsOptions(),
    enabled: isAuthSetupComplete && isGovernment,
  });

  // Mutations
  const createQuestionMutationFn = useMutation({
    ...createLicenseTypeQuestionMutation(),
  });

  const updateQuestionMutationFn = useMutation({
    ...updateLicenseTypeQuestionMutation(),
  });

  const handleSubmit = async (data: any) => {
    // Note: data.scope is backend QuestionScope (converted from simplified scope in the form)
    // Make sure we reset answer options if they are not appropriate for the answer type
    if (
      data.answer_type !== "single_select" &&
      data.answer_type !== "multi_select" &&
      data.answer_type !== "boolean"
    ) {
      data.answer_options = {};
      data.answer_option_keys_order = null;
    }

    if (!questionId) {
      try {
        const isJurisdictionScope = data.scope === "jurisdiction";
        const isGroupScope = data.scope === "group";

        if (isJurisdictionScope && !data.jurisdiction_id) {
          toast({
            title: "Error",
            description:
              "Jurisdiction is required for creating jurisdiction-specific questions",
            variant: "destructive",
          });
          return;
        }

        if (isGroupScope && !data.question_group_id) {
          toast({
            title: "Error",
            description:
              "Question group is required for creating group questions",
            variant: "destructive",
          });
          return;
        }

        const baseFields = {
          license_type_id: data.license_type_id,
          question: data.question,
          answer_type: data.answer_type,
          answer_options: data.answer_options,
          answer_option_keys_order:
            data.answer_type === "single_select" ||
            data.answer_type === "multi_select" ||
            data.answer_type === "boolean"
              ? (data.answer_option_keys_order ?? null)
              : null,
          display_order: data.display_order ?? 0,
          jump_target: data.jump_target ?? null,
          information_text: data.information_text,
          answer_required: data.answer_required,
          display_up_front: data.display_up_front,
          show_on_fees_payment_screen: data.show_on_fees_payment_screen,
          select_from_fixed_list: data.select_from_fixed_list,
          allow_copy_registrant_info: data.allow_copy_registrant_info ?? true,
          suggest_previous_property_managers:
            data.answer_type === "company_contact"
              ? (data.suggest_previous_property_managers ?? false)
              : false,
          copy_from_question_ids:
            data.answer_type === "company_contact"
              ? (data.copy_from_question_ids ?? [])
              : [],
          contact_name_required: data.contact_name_required,
          contact_address_required: data.contact_address_required,
          contact_email_required: data.contact_email_required,
          contact_phone_required: data.contact_phone_required,
          contact_company_name_required: data.contact_company_name_required,
          contact_require_company_or_person_name:
            data.contact_require_company_or_person_name,
          use_contact_label: data.use_contact_label ?? false,
          url_template: data.url_template ? data.url_template : null,
          blank_answer_url: data.blank_answer_url
            ? data.blank_answer_url
            : null,
          http_method: data.http_method ?? "GET",
          value_extract_regex: data.value_extract_regex?.trim()
            ? data.value_extract_regex.trim()
            : null,
          request_fields:
            data.request_fields && Object.keys(data.request_fields).length > 0
              ? data.request_fields
              : null,
          browser_helper_config:
            data.answer_type === "live_link" && data.http_method === "EXTENSION"
              ? (data.browser_helper_config ?? null)
              : null,
          copy_value_to_clipboard: data.copy_value_to_clipboard ?? false,
          allow_multiple_photos: data.allow_multiple_photos ?? false,
          allow_multiple_urls: data.allow_multiple_urls ?? false,
          allow_multiple_documents: data.allow_multiple_documents ?? false,
          ...companyShowInListPayload(data),
          question_type: questionType,
        };

        if (data.scope === "jurisdiction") {
          await createQuestionMutationFn.mutateAsync({
            body: {
              scope: "jurisdiction",
              ...baseFields,
              jurisdiction_id: data.jurisdiction_id as string,
            },
          });
        } else if (data.scope === "group") {
          await createQuestionMutationFn.mutateAsync({
            body: {
              scope: "group",
              ...baseFields,
              question_group_id: data.question_group_id as string,
            },
          });
        } else {
          await createQuestionMutationFn.mutateAsync({
            body: {
              scope: "global",
              ...baseFields,
            },
          });
        }
        toast({
          title: "Success",
          description: "Question created successfully!",
          variant: "secondary",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: `Failed to create question: ${getQueryErrorMessage(error)}`,
          variant: "destructive",
        });
        throw error;
      }
    } else {
      if (!scope) {
        toast({
          title: "Error",
          description: "Scope is required for updating questions",
          variant: "destructive",
        });
        return;
      }

      try {
        // Update question - use explicit booleans so fields are never omitted from JSON (undefined is stripped by JSON.stringify)
        const updateBody = {
          question: data.question,
          answer_type: data.answer_type,
          answer_options: data.answer_options,
          answer_option_keys_order:
            data.answer_type === "single_select" ||
            data.answer_type === "multi_select" ||
            data.answer_type === "boolean"
              ? (data.answer_option_keys_order ?? null)
              : null,
          display_order: data.display_order,
          jump_target: data.jump_target ?? null,
          license_type_id: data.license_type_id,
          is_deleted: data.is_deleted,
          information_text: data.information_text,
          answer_required: data.answer_required,
          display_up_front: data.display_up_front ?? false,
          show_on_fees_payment_screen:
            data.show_on_fees_payment_screen ?? false,
          select_from_fixed_list: data.select_from_fixed_list ?? false,
          allow_copy_registrant_info: data.allow_copy_registrant_info ?? true,
          suggest_previous_property_managers:
            data.answer_type === "company_contact"
              ? (data.suggest_previous_property_managers ?? false)
              : false,
          copy_from_question_ids:
            data.answer_type === "company_contact"
              ? (data.copy_from_question_ids ?? [])
              : [],
          contact_name_required: data.contact_name_required,
          contact_address_required: data.contact_address_required,
          contact_email_required: data.contact_email_required,
          contact_phone_required: data.contact_phone_required,
          contact_company_name_required: data.contact_company_name_required,
          contact_require_company_or_person_name:
            data.contact_require_company_or_person_name,
          use_contact_label: data.use_contact_label ?? false,
          url_template: data.url_template ? data.url_template : null,
          blank_answer_url: data.blank_answer_url
            ? data.blank_answer_url
            : null,
          http_method: data.http_method ?? "GET",
          value_extract_regex: data.value_extract_regex?.trim()
            ? data.value_extract_regex.trim()
            : null,
          request_fields:
            data.request_fields && Object.keys(data.request_fields).length > 0
              ? data.request_fields
              : null,
          browser_helper_config:
            data.answer_type === "live_link" && data.http_method === "EXTENSION"
              ? (data.browser_helper_config ?? null)
              : null,
          copy_value_to_clipboard: data.copy_value_to_clipboard ?? false,
          allow_multiple_photos: data.allow_multiple_photos ?? false,
          allow_multiple_urls: data.allow_multiple_urls ?? false,
          allow_multiple_documents: data.allow_multiple_documents ?? false,
          ...companyShowInListPayload(data),
        };

        await updateQuestionMutationFn.mutateAsync({
          path: {
            question_scope: scope as QuestionScope,
            question_id: questionId as string,
          },
          body: updateBody,
        });
        const wasDeleted = questionData?.is_deleted === true;
        const nowDeleted = data.is_deleted === true;
        let description = "Question updated successfully!";
        if (!wasDeleted && nowDeleted) {
          description = "Question deactivated successfully!";
        } else if (wasDeleted && !nowDeleted) {
          description = "Question reactivated successfully!";
        }
        toast({
          title: "Success",
          description,
          variant: "secondary",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: `${getQueryErrorMessage(error)}`,
          variant: "destructive",
        });
        throw error;
      }
    }

    // Drop Cases-list cache so live-link helper config is not shown stale.
    if (data.answer_type === "live_link") {
      dropForeclosureCaseListQueries(queryClient);
    }
    void queryClient.invalidateQueries();
  };

  return {
    // Data
    questionData: questionData
      ? {
          scope: questionData.scope,
          license_type_id: questionData.license_type_id,
          license_type_name: questionData.license_type_name,
          jurisdiction_id: questionData.jurisdiction_id,
          jurisdiction_name: questionData.jurisdiction_name,
          question_group_id: questionData.question_group_id ?? null,
          question: questionData.question,
          answer_type: questionData.answer_type,
          answer_options: questionData.answer_options,
          answer_option_keys_order:
            questionData.answer_option_keys_order ?? null,
          display_order: questionData.display_order,
          jump_target: questionData.jump_target ?? null,
          is_deleted: questionData.is_deleted,
          information_text: questionData.information_text || "",
          answer_required: questionData.answer_required,
          display_up_front: questionData.display_up_front,
          show_on_fees_payment_screen: questionData.show_on_fees_payment_screen,
          select_from_fixed_list: questionData.select_from_fixed_list,
          allow_copy_registrant_info:
            (questionData as { allow_copy_registrant_info?: boolean })
              .allow_copy_registrant_info ?? true,
          contact_name_required:
            (questionData as { contact_name_required?: boolean })
              .contact_name_required ?? false,
          contact_address_required:
            (questionData as { contact_address_required?: boolean })
              .contact_address_required ?? false,
          contact_email_required:
            (questionData as { contact_email_required?: boolean })
              .contact_email_required ?? false,
          contact_phone_required:
            (questionData as { contact_phone_required?: boolean })
              .contact_phone_required ?? false,
          contact_company_name_required:
            (questionData as { contact_company_name_required?: boolean })
              .contact_company_name_required ?? false,
          contact_require_company_or_person_name:
            (
              questionData as {
                contact_require_company_or_person_name?: boolean;
              }
            ).contact_require_company_or_person_name ?? false,
          use_contact_label:
            (questionData as { use_contact_label?: boolean })
              .use_contact_label ?? false,
          suggest_previous_property_managers:
            (
              questionData as {
                suggest_previous_property_managers?: boolean;
              }
            ).suggest_previous_property_managers ?? false,
          copy_from_question_ids:
            (questionData as { copy_from_question_ids?: string[] | null })
              .copy_from_question_ids ?? [],
          url_template:
            (questionData as { url_template?: string | null }).url_template ??
            "",
          blank_answer_url:
            (questionData as { blank_answer_url?: string | null })
              .blank_answer_url ?? "",
          http_method:
            (
              questionData as {
                http_method?: "GET" | "POST" | "EXTENSION" | null;
              }
            ).http_method ?? "GET",
          value_extract_regex:
            (questionData as { value_extract_regex?: string | null })
              .value_extract_regex ?? "",
          request_fields:
            (
              questionData as {
                request_fields?: Record<string, string> | null;
              }
            ).request_fields ?? null,
          browser_helper_config:
            (
              questionData as {
                browser_helper_config?: Record<string, unknown> | null;
              }
            ).browser_helper_config ?? null,
          copy_value_to_clipboard:
            (questionData as { copy_value_to_clipboard?: boolean })
              .copy_value_to_clipboard ?? false,
          allow_multiple_photos:
            (questionData as { allow_multiple_photos?: boolean })
              .allow_multiple_photos ?? false,
          allow_multiple_urls:
            (questionData as { allow_multiple_urls?: boolean })
              .allow_multiple_urls ?? false,
          allow_multiple_documents:
            (questionData as { allow_multiple_documents?: boolean })
              .allow_multiple_documents ?? false,
          show_in_list:
            (questionData as { show_in_list?: boolean }).show_in_list ?? false,
          contact_name_show_in_list:
            (questionData as { contact_name_show_in_list?: boolean })
              .contact_name_show_in_list ?? false,
          contact_company_name_show_in_list:
            (questionData as { contact_company_name_show_in_list?: boolean })
              .contact_company_name_show_in_list ?? false,
          contact_email_show_in_list:
            (questionData as { contact_email_show_in_list?: boolean })
              .contact_email_show_in_list ?? false,
          contact_phone_show_in_list:
            (questionData as { contact_phone_show_in_list?: boolean })
              .contact_phone_show_in_list ?? false,
          contact_address_show_in_list:
            (questionData as { contact_address_show_in_list?: boolean })
              .contact_address_show_in_list ?? false,
        }
      : null,

    jurisdictionsData: isOperator
      ? (jurisdictionsData?.jurisdictions ?? [])
      : isGovernment && governmentJurisdictionData
        ? [
            {
              id: governmentJurisdictionData.id,
              name: governmentJurisdictionData.name,
              license_type_configurations:
                (
                  governmentJurisdictionData as {
                    license_type_configurations?: Array<{
                      license_type_id: string;
                    }>;
                  }
                ).license_type_configurations ?? [],
            },
          ]
        : [],

    licenseTypesData: isGovernment
      ? (governmentLicenseTypeConfigsData?.map((config) => ({
          id: config.license_type_id,
          name: config.license_type_name ?? "",
        })) ?? [])
      : (licenseTypesData?.license_types?.map((licenseType) => ({
          id: licenseType.id,
          name: licenseType.name,
        })) ?? []),

    // Loading states
    isLoading:
      isLoadingQuestion ||
      isLoadingJurisdictions ||
      (isGovernment
        ? isLoadingGovernmentLicenseTypeConfigs
        : isLoadingLicenseTypes),

    // Errors
    error:
      errorQuestion ||
      errorJurisdictions ||
      (isGovernment ? errorGovernmentLicenseTypeConfigs : errorLicenseTypes),

    // Actions
    handleSubmit,
  };
}
